import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import PublicNavbar from "@/components/PublicNavbar";

import Home from "@/pages/public/Home";
import HallOfFame from "@/pages/public/HallOfFame";
import AdminHallOfFame from "@/pages/admin/HallOfFame";
import Tournaments from "@/pages/public/Tournaments";
import ExperimentalClass from "@/pages/public/ExperimentalClass";
import Login from "@/pages/public/Login";

import AthleteDashboard from "@/pages/athlete/Dashboard";
import AthleteProfile from "@/pages/athlete/Profile";
import Feedback from "@/pages/athlete/Feedback";
import Attendance from "@/pages/athlete/Attendance";
import Games from "@/pages/athlete/Games";
import Videos from "@/pages/athlete/Videos";
import Evolution from "@/pages/athlete/Evolution";
import Ranking from "@/pages/athlete/Ranking";
import AthleteTournaments from "@/pages/athlete/Tournaments";
import AthletePlan from "@/pages/athlete/Plan";

import AdminDashboard from "@/pages/admin/Dashboard";
import AdminAthletes from "@/pages/admin/Athletes";
import AdminTournaments from "@/pages/admin/Tournaments";
import AdminExperimental from "@/pages/admin/ExperimentalRequests";
import AdminVideos from "@/pages/admin/Videos";
import AdminAttendance from "@/pages/admin/Attendance";
import AdminCompetitions from "@/pages/admin/Competitions";
import AdminRanking from "@/pages/admin/Ranking";
import AdminRankingConfig from "@/pages/admin/RankingConfig";
import AdminFeedback from "@/pages/admin/Feedback";

// Roles como constantes estáveis — evita nova referência em cada render.
const ATHLETE_ROLES = ["athlete"];
const ADMIN_ROLES = ["admin", "coach"];
const PUBLIC_PAGES = new Set(["/", "/torneios", "/hall-da-fama", "/aula-experimental"]);

function PersistentPublicNavbar() {
  const { pathname } = useLocation();
  return PUBLIC_PAGES.has(pathname) ? <PublicNavbar /> : null;
}

function App() {
  useEffect(() => {
    document.title = "gruposantodexadrez — Grupo Santo de Xadrez";
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
        <PersistentPublicNavbar />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/torneios" element={<Tournaments />} />
          <Route path="/hall-da-fama" element={<HallOfFame />} />
          <Route path="/aula-experimental" element={<ExperimentalClass />} />
          <Route path="/programa-evolucao" element={<Navigate to="/atleta/plano" replace />} />
          <Route path="/area-do-aluno" element={<Navigate to="/atleta/plano" replace />} />
          <Route path="/plano" element={<Navigate to="/atleta/plano" replace />} />
          <Route path="/login" element={<Login />} />

          <Route path="/atleta" element={<ProtectedRoute roles={ATHLETE_ROLES}><AthleteDashboard /></ProtectedRoute>} />
          <Route path="/atleta/perfil" element={<ProtectedRoute roles={ATHLETE_ROLES}><AthleteProfile /></ProtectedRoute>} />
          <Route path="/atleta/feedback" element={<ProtectedRoute roles={ATHLETE_ROLES}><Feedback /></ProtectedRoute>} />
          <Route path="/atleta/frequencia" element={<ProtectedRoute roles={ATHLETE_ROLES}><Attendance /></ProtectedRoute>} />
          <Route path="/atleta/partidas" element={<ProtectedRoute roles={ATHLETE_ROLES}><Games /></ProtectedRoute>} />
          <Route path="/atleta/videos" element={<ProtectedRoute roles={ATHLETE_ROLES}><Videos /></ProtectedRoute>} />
          <Route path="/atleta/evolucao" element={<ProtectedRoute roles={ATHLETE_ROLES}><Evolution /></ProtectedRoute>} />
          <Route path="/atleta/ranking" element={<ProtectedRoute roles={ATHLETE_ROLES}><Ranking /></ProtectedRoute>} />
          <Route path="/atleta/torneios" element={<ProtectedRoute roles={ATHLETE_ROLES}><AthleteTournaments /></ProtectedRoute>} />
          <Route path="/atleta/plano" element={<ProtectedRoute roles={ATHLETE_ROLES}><AthletePlan /></ProtectedRoute>} />

          <Route path="/admin" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/atletas" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminAthletes /></ProtectedRoute>} />
          <Route path="/admin/feedbacks" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminFeedback /></ProtectedRoute>} />
          <Route path="/admin/torneios" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminTournaments /></ProtectedRoute>} />
          <Route path="/admin/hall-da-fama" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminHallOfFame /></ProtectedRoute>} />
          <Route path="/admin/aulas-experimentais" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminExperimental /></ProtectedRoute>} />
          <Route path="/admin/videos" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminVideos /></ProtectedRoute>} />
          <Route path="/admin/frequencia" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminAttendance /></ProtectedRoute>} />
          <Route path="/admin/competicoes" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminCompetitions /></ProtectedRoute>} />
          <Route path="/admin/ranking" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminRanking /></ProtectedRoute>} />
          <Route path="/admin/ranking-config" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminRankingConfig /></ProtectedRoute>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen bg-[#061426] text-slate-100 flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        <div className="text-amber-400 font-mono text-6xl">404</div>
        <h1 className="text-3xl text-white font-semibold mt-4">Página não encontrada</h1>
        <p className="text-slate-400 mt-2">A rota acessada não existe no GSX.</p>
        <a href="/" className="inline-block mt-6 px-5 py-2.5 rounded-md bg-amber-500 text-[#071A33] font-semibold hover:bg-amber-400">Voltar ao início</a>
      </div>
    </div>
  );
}

export default App;
